import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	Input,
	NgZone,
	Output,
} from "@angular/core";

import {
	GenericKendoGridColumnDefinition,
	LookupEntityList,
	MetadataSet,
	ParsedFieldMetadataDetail,
	ParsedMetadataSet,
} from "../../../insa-models";
import {
	AuthService,
	InsaMetadataService,
	InsaService,
	LocalizeService,
} from "../../../insa-services";
import {
	customGroupHeader,
	CustomGridFilterModule,
	SharedMethodsModule,
} from "../../../insa-custom-scripts";

import "./generic-grid.css";
import { ActivatedRoute, Router } from "@angular/router";
import { makeParsedMetadataSet } from "../utils/parsedMetadataSet";
import computeKeyFields from "../utils/keyFields";
import { combineLatest, Observable, Subscription } from "rxjs";
import { values } from "core-js/core/array";
import { GridStateService } from "../../../insa-services/gridState.service";
import { HttpClient } from "@angular/common/http";
import { appConfig } from "../../../../insaweb.config";
import { appStateProvider } from "../../../insa-providers";

let cloneDeep = require("lodash.clonedeep");

// this number is multiplied with FieldWidth from metadata
const FIELDWIDTH_MULTIPLIER = 5;
const GRID_DATE_SHORT_FORMAT = "dd.MM.yyyy";
const GRID_DATE_LONG_FORMAT = "dd.MM.yyyy hh:mm:ss";
const GRID_DATE_TIME_FORMAT = "dd.MM.yyyy HH:mm:ss";
const GRID_NUMBER_FORMAT = "{0:#.##}";
const GRID_DEFAULT_WIDTH = 120;

@Component({
	selector: "generic-grid",
	templateUrl: "generic-grid.component.html",
	styles: [require("./generic-grid.component.css").toString()],
})
export class GenericGridComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() metadataName?: string; // either `data` or `metadataName` required for rendering
	@Input() data?: any[];
	@Input() onItemSelect?: (entityId: number | string) => undefined;
	@Input() canAdd: boolean = true; // overrides metadata settings if allowed
	@Input() canDelete: boolean = true; // overrides metadata settings if allowed

	gridName = "genericGrid";
	private hasKey = false;
	isGridWizardVisible: boolean = false;
	private _lastFromDate: Date = null;
	private _lastToDate: Date = null;
	private _pendingCancelCallback: (() => void) | null = null;
	private _pendingCancelOrderIndex: any = null;
	private _pendingReportOrderIndex: any = null;
	private _orderPdfBlobUrl: string = null;
	private _appStateSubscription: Subscription = null;
	// Serialises metadata loads: ngOnChanges and the appState subscription can both
	// fire on first render. Only the latest load may build the grid; earlier ones
	// are cancelled/ignored so two async builds never interleave and leave a
	// half-rendered grid (blank columns until a reroute/refresh).
	private _loadToken = 0;
	private _metadataSubscription: Subscription = null;

	selectedId: any;
	metadataSet?: ParsedMetadataSet;
	keyFields?: string[];
	gridKendoOptions: kendo.ui.GridOptions;
	defaultLayout;
	aggregates = [];

	constructor(
		private route: ActivatedRoute,
		private _router: Router,
		private _zone: NgZone,
		private _insaMetadataService: InsaMetadataService,
		private _activeRoute: ActivatedRoute,
		private _localize: LocalizeService,
		private _authService: AuthService,
		private _gridStateService: GridStateService,
		private _http: HttpClient,
		private _appStateProvider: appStateProvider,
		private _insaService: InsaService,
		private _element: ElementRef,
	) {
		this.doubleClickHandler = this.doubleClickHandler.bind(this);
		this.delPressHandler = this.delPressHandler.bind(this);
		this.openGridWizard = this.openGridWizard.bind(this);
	}

	applyGridSettings(newOptions) {
		const kendoGrid = $("#genericGrid").data("kendoGrid");

		// add customCompare functions if necessary
		newOptions.columns = SharedMethodsModule.CustomizeGroupOrSortSettings(
			kendoGrid,
			newOptions.columns,
		);

		// append action collumns (edit/delete)
		const currOptions = kendoGrid.getOptions();
		const actionColumns = currOptions.columns.filter((col) => !col.field);
		if (actionColumns.length) {
			newOptions.columns.push(...actionColumns);
		}

		kendoGrid.setOptions(newOptions);
		setTimeout(() => {
			$("#custom-grid-wizard-toggle").off("click", this.openGridWizard);
			$("#custom-grid-wizard-toggle").click(this.openGridWizard);
		}, 100);
	}

	// ===GETTERS===
	get routeName() {
		return this._activeRoute.snapshot.params.metadataName;
	}

	deleteItem(entityId) {
		if (this.canDelete) {
			this.loadingStarted();
			this._insaMetadataService
				.deleteGenericEntity(entityId, this.metadataName)
				.subscribe(
					() => {
						this._insaMetadataService
							.getMetaDataInformation(this.metadataName)
							.subscribe((metadataSet: MetadataSet) => {
								const kendoGrid = $("#" + this.gridName).data("kendoGrid");
								if (kendoGrid) {
									kendoGrid.setDataSource(
										new kendo.data.DataSource({
											data: metadataSet[1].MetaDataSelectedEntity,
										}),
									);
									this.loadingStopped();
								}
							});
					},
					(errorMessage) => {
						this.handleError(errorMessage);
					},
				);
		} else {
			alert(`Delete not allowed on items from ${this.metadataName}`);
		}
	}

	// HELPER METHODS
	goToDetailsPage(id: string | number) {
		this._router.navigate([this._router.url, id]);
	}

	doubleClickHandler() {
		const grid = $("#" + this.gridName).data("kendoGrid");
		const selectedItem = grid.dataItem(grid.select());
		if (selectedItem) {
			if (this.keyFields[0]) {
				this.onItemSelect(selectedItem[this.keyFields[0]]);
			} else {
				this.handleError(
					`${this.metadataName} is missing a primary key in its Metadata details`,
				);
			}
		}
	}

	delPressHandler(evt) {
		if (evt.keyCode == 46 || evt.key === "Delete") {
			const grid = $("#" + this.gridName).data("kendoGrid");
			const selectedItem = grid.dataItem(grid.select());
			if (selectedItem) {
				const isOk = confirm("Are you sure you want to delete this record?");
				if (isOk) {
					this.deleteItem(selectedItem[this.keyFields[0]]);
				}
			}
		}
	}

	showModal({ title = "", message = "" }) {
		const modalEl = $("#generic-form-modal");
		modalEl.find(".modal-title").text(title);
		modalEl.find(".modal-body > p").text(message);
		modalEl.modal({ show: true });
	}

	private prepareGenericForm(metadataSet: ParsedMetadataSet, defaultLayout) {
		const recordSet = metadataSet[1].MetaDataSelectedEntity;
		const metadataDetails = metadataSet[0].InsaMetaDataDetails;
		const lookupEntityList = metadataSet[1].MetaDataLookupEntity;
		const formMetadata = metadataSet[0].InsaMetaData[0];

		this.canAdd = this.canAdd
			? formMetadata.AllowAddNew === "True"
			: this.canAdd;
		this.canDelete = this.canDelete
			? formMetadata.AllowDelete === "True"
			: this.canDelete;

		let gridSource = this.prepareGridSource(recordSet, metadataDetails);

		// make column defs
		const additionalSortingFields = metadataDetails.filter(
			(md) => md.SortAdditionalField,
		);
		let columnDefs;
		if (defaultLayout.options) {
			// Fix: guard against col being undefined before accessing .groupable/.sortable
			if (additionalSortingFields.length) {
				additionalSortingFields.forEach((md) => {
					const col = defaultLayout.options.columns.find(
						(dcol) => dcol.field === md.FieldTitle,
					);
					if (!col) return;
					if (col.groupable) {
						col.groupable.sort.compare =
							SharedMethodsModule.makeCustomCompareFunction(
								md.SortAdditionalField,
							);
					}
					if (col.sortable) {
						col.sortable.compare =
							SharedMethodsModule.makeCustomCompareFunction(
								md.SortAdditionalField,
							);
					}
				});
			}
			const savedCols = defaultLayout.options.columns.filter((col) => {
				if (!col.field) return true;
				const md = metadataDetails.find((m) => m.FieldName === col.field);
				return md ? Boolean(md.VisibleOnGrid) : false;
			});
			// If the saved layout has no data columns (stale/mismatched layout),
			// fall back to fresh column generation from current metadata
			const hasDataColumns = savedCols.some((col) => !!col.field);
			if (hasDataColumns) {
				columnDefs = savedCols;
				this.applyLookupTemplates(
					columnDefs,
					metadataDetails,
					lookupEntityList,
				);
				this.applyDateSortCompare(columnDefs, metadataDetails);
			} else {
				columnDefs = this.createGridColumnDef(
					metadataDetails,
					lookupEntityList,
				);
			}
		} else {
			columnDefs = this.createGridColumnDef(metadataDetails, lookupEntityList);
		}

		// initialize
		const kendoGrid = $("#" + this.gridName).data("kendoGrid");
		if (kendoGrid) {
			kendoGrid.destroy();
		}
		this.initGrid(columnDefs, gridSource, metadataDetails, defaultLayout);
		this.registerDoubleClickHandler();
		this.registerDelPressHandler();
	}

	prepareGridSource(recordSet, metadataDetails) {
		let data = recordSet;
		let schema = {
			model: {
				id: "",
				fields: {},
			},
		};
		metadataDetails.forEach((md) => {
			if (md.IsKey) {
				schema.model.id = md.FieldName;
			}
			schema.model.fields[md.FieldName] = { type: md.JavascriptType };
		});

		return { data: data, schema: schema };
	}

	private createGridColumnDef(
		metadataDetails: ParsedFieldMetadataDetail[],
		lookupEntityList: LookupEntityList,
	) {
		const gridColumnDefs = new Array<any>();

		metadataDetails
			.filter((md) => md.VisibleOnGrid && md.OrderID !== 0)
			.forEach((md) => {
				let fmtType = md.JavascriptType;
				let metaFormat = md.Format ? md.Format.trim().toUpperCase() : undefined;
				if (metaFormat === "") {
					metaFormat = undefined;
				}

				const chosenFormat = metaFormat || fmtType;

				const fieldName = md.FieldName.replace(" ", "_");

				const customCompareFunction = md.SortAdditionalField
					? SharedMethodsModule.makeCustomCompareFunction(
							md.SortAdditionalField,
						)
					: null;

				const title = md.FieldTitle;
				let format: string;
				const filterable = true;
				// const aggregates = md.ShowTotals ? ['sum'] : null;
				let aggregates = null;
				if (md.ShowTotals == true) aggregates = ["sum"];
				if (aggregates) {
					this.aggregates.push({ field: md.FieldName, aggregate: "sum" });
				}
				const footerTemplate = aggregates
					? '<div style="float: right">#= (sum!==null) ? kendo.toString(sum, "n2") : "" #</div>'
					: null;
				const groupFooterTemaplte = aggregates
					? '<div style="float: right">#= (sum!==null) ? kendo.toString(sum, "n2") : "" #</div>'
					: null;
				const hidden = !md.DefaultVisibility;
				const groupable = md.GroupEnabled
					? customCompareFunction
						? { sort: { compare: customCompareFunction } }
						: md.GroupEnabled
					: false;
				const width = Math.max(
					md.FieldWidth * FIELDWIDTH_MULTIPLIER,
					GRID_DEFAULT_WIDTH,
				);
				const headerAttributes = {};
				let sortable: any = md.SortEnabled
					? customCompareFunction
						? { compare: customCompareFunction }
						: md.SortEnabled
					: false;
				let template: string | Function;
				// const editable = Boolean(md.Locked);
				const editable = false;
				let values;

				switch (chosenFormat) {
					case "DATETIME":
					case "dd.MM.yyyy HH:mm:ss":
					case "datetime":
					case "date":
						format = "";
						template = `#:(${md.FieldName}!==null) ? kendo.toString(${md.FieldName}, '${GRID_DATE_TIME_FORMAT}') : '' #`;
						break;
					case "LONGDATE":
					case "DATE":
					case "dd.MM.yyyy":
						format = "dd.MM.yyyy";
						template = `#:(${md.FieldName}!==null) ? kendo.toString(${md.FieldName}, '${GRID_DATE_SHORT_FORMAT}') : '' #`;
						break;
					case "PERCENT":
						format = `'${GRID_NUMBER_FORMAT} %'`;
						template = `#:(${md.FieldName}!==null) ? kendo.toString(${md.FieldName} * 100, 'n2') : '' #`;
						break;
					case "#,##00.00":
					case "NUMBER":
					case "AMOUNT":
					case "QUANTITY":
						format = `'${GRID_NUMBER_FORMAT}'`;
						template = `#:(${md.FieldName}!==null) ? kendo.toString(${md.FieldName}, 'n2') : '' #`;
						break;
					case "CHECKBOX":
					case "boolean":
						format = ``;
						template = `<i class="glyphicon glyphicon-#:${md.FieldName} ? "ok" : "remove" #"></i>`;
						break;
					// by default number is not formatted
					case "TEXT":
					case "number":
					case "MAILBUTTON":
					case "SELECTOR":
					case "IMAGE":
					case "string":
					case "":
					case undefined:
					default:
						format = "";
						if (md.LookupTable != null && md.LookupTable.trim() !== "") {
							if (lookupEntityList.length > 0) {
								const isItemData =
									md.LookupTable.trim().toLowerCase() === "itemdata";
								const matchedEntity = lookupEntityList.find((le) =>
									isItemData
										? le.MetadataFieldname === md.FieldName
										: le.lookupTableName === md.LookupTable,
								);
								if (matchedEntity) {
									const lookupMap = new Map<any, string>(
										matchedEntity.lookupData.map((item) => [
											item[matchedEntity.lookupField],
											item[matchedEntity.lookupDisplayField],
										]),
									);
									values = Array.from(lookupMap.entries()).map(([v, t]) => ({
										value: v,
										text: t,
									}));
									const fieldName = md.FieldName;
									template = (dataItem: any) => {
										const raw = dataItem[fieldName];
										return (
											lookupMap.get(raw) ??
											(raw !== null && raw !== undefined ? String(raw) : "")
										);
									};
								}
							}
						} else if (chosenFormat && chosenFormat.startsWith("#")) {
							format = `{0:${chosenFormat}}`;
							template = `#:(${md.FieldName}!==null) ? kendo.toString(${md.FieldName}, '${chosenFormat}') : '' #`;
						} else {
							template = `#:(${md.FieldName}!==null) ? ${md.FieldName} : '' #`;
						}
				}

				const _isDateField =
					md.JavascriptType === "date" ||
					chosenFormat === "DATETIME" ||
					chosenFormat === "dd.MM.yyyy HH:mm:ss" ||
					chosenFormat === "datetime" ||
					chosenFormat === "date" ||
					chosenFormat === "LONGDATE" ||
					chosenFormat === "DATE" ||
					chosenFormat === "dd.MM.yyyy";
				if (_isDateField && md.SortEnabled) {
					const _field = md.FieldName;
					sortable = {
						compare: (a: any, b: any) => {
							const _ms = (v: any) => {
								if (v == null) return 0;
								if (v instanceof Date)
									return isNaN(v.getTime()) ? 0 : v.getTime();
								const d = kendo.parseDate(v as string) ?? new Date(v);
								return isNaN(d.getTime()) ? 0 : d.getTime();
							};
							return _ms(a[_field]) - _ms(b[_field]);
						},
					};
				}

				const column = new GenericKendoGridColumnDefinition(
					fieldName,
					title,
					format,
					filterable,
					undefined,
					aggregates,
					footerTemplate,
					groupFooterTemaplte,
					undefined,
					hidden,
					undefined,
					groupable,
					width,
					headerAttributes,
					sortable,
					template,
					editable,
				);

				if (values) {
					column.values = values;
					column.filterable = { multi: true, search: true };
				}

				gridColumnDefs.push(column);
			});
		return gridColumnDefs;
	}

	private applyDateSortCompare(
		columnDefs: any[],
		metadataDetails: ParsedFieldMetadataDetail[],
	) {
		const _ms = (v: any) => {
			if (v == null) return 0;
			if (v instanceof Date) return isNaN(v.getTime()) ? 0 : v.getTime();
			const d = kendo.parseDate(v as string) ?? new Date(v);
			return isNaN(d.getTime()) ? 0 : d.getTime();
		};
		const DATE_FORMATS = new Set([
			"DATETIME",
			"datetime",
			"date",
			"dd.MM.yyyy HH:mm:ss",
			"DD.MM.YYYY HH:MM:SS",
			"LONGDATE",
			"DATE",
			"dd.MM.yyyy",
			"DD.MM.YYYY",
		]);
		columnDefs.forEach((col) => {
			if (!col.field) return;
			const md = metadataDetails.find(
				(m) =>
					m.FieldName === col.field ||
					m.FieldName.replace(" ", "_") === col.field,
			);
			if (!md || !md.SortEnabled) return;
			const fmt = ((md as any).Format || "").trim();
			if (
				md.JavascriptType !== "date" &&
				!DATE_FORMATS.has(fmt) &&
				!DATE_FORMATS.has(fmt.toUpperCase())
			)
				return;
			const field = md.FieldName;
			col.sortable = {
				compare: (a: any, b: any) => _ms(a[field]) - _ms(b[field]),
			};
		});
	}

	private applyLookupTemplates(
		columnDefs: any[],
		metadataDetails: ParsedFieldMetadataDetail[],
		lookupEntityList: LookupEntityList,
	) {
		columnDefs.forEach((col) => {
			if (!col.field) return;
			const md = metadataDetails.find((m) => m.FieldName === col.field);
			if (!md || !md.LookupTable || md.LookupTable.trim() === "") return;
			const isItemData = md.LookupTable.trim().toLowerCase() === "itemdata";
			const matchedEntity = lookupEntityList.find((le) =>
				isItemData
					? le.MetadataFieldname === md.FieldName
					: le.lookupTableName === md.LookupTable,
			);
			if (!matchedEntity) return;
			const lookupMap = new Map<any, string>(
				matchedEntity.lookupData.map((item) => [
					item[matchedEntity.lookupField],
					item[matchedEntity.lookupDisplayField],
				]),
			);
			col.values = Array.from(lookupMap.entries()).map(([v, t]) => ({
				value: v,
				text: t,
			}));
			const fieldName = md.FieldName;
			col.template = (dataItem: any) => {
				const raw = dataItem[fieldName];
				return (
					lookupMap.get(raw) ??
					(raw !== null && raw !== undefined ? String(raw) : "")
				);
			};
		});
	}

	registerDoubleClickHandler() {
		$("#" + this.gridName).off("dblclick", this.doubleClickHandler);
		if (this.onItemSelect) {
			$("#" + this.gridName).on("dblclick", this.doubleClickHandler);
		}
	}

	registerDelPressHandler() {
		$("html").off("keydown", this.delPressHandler);
		$("html").keydown(this.delPressHandler);
	}

	openGridWizard() {
		this.isGridWizardVisible = true;
	}

	closeGridWizard() {
		this.isGridWizardVisible = false;
	}

	private initGrid(
		columnDefs,
		gridSource,
		metadataDetails: ParsedFieldMetadataDetail[],
		defaultLayout,
	) {
		let dataSourceOptions: kendo.data.DataSourceOptions = {};
		dataSourceOptions.data = gridSource.data;
		dataSourceOptions.schema = gridSource.schema;
		if (defaultLayout.options && defaultLayout.options.dataSource.group) {
			dataSourceOptions.group =
				SharedMethodsModule.CustomizeGroupOrSortSettings(
					{ columns: columnDefs },
					defaultLayout.options.dataSource.group,
				);
		}
		if (defaultLayout.options && defaultLayout.options.dataSource.sort) {
			dataSourceOptions.sort = SharedMethodsModule.CustomizeGroupOrSortSettings(
				{ columns: columnDefs },
				defaultLayout.options.dataSource.sort,
			);
		}
		if (
			this.metadataName === "OrdersWindowWeb" &&
			Array.isArray(gridSource.data)
		) {
			const entryDateField =
				metadataDetails.find((md) => md.FieldName.toLowerCase() === "entrydate")
					?.FieldName ?? "EntryDate";
			const _ms = (v: any) => {
				if (v == null) return 0;
				if (v instanceof Date) return isNaN(v.getTime()) ? 0 : v.getTime();
				const d = kendo.parseDate(v as string) ?? new Date(v);
				return isNaN(d.getTime()) ? 0 : d.getTime();
			};
			// Sort in-place — transport.read uses gridSource.data by reference
			(gridSource.data as any[]).sort(
				(a, b) => _ms(b[entryDateField]) - _ms(a[entryDateField]),
			);
			dataSourceOptions.sort = undefined;
		}
		dataSourceOptions.aggregate = metadataDetails
			.filter((md) => md.ShowTotals && md.VisibleOnGrid)
			.map((md) => ({ field: md.FieldName, aggregate: "sum" }));

		dataSourceOptions.transport = {
			read: function (e) {
				e.success(gridSource.data);
			},
			update: function (e) {},
			create: function (e) {},
			destroy: function (e) {},
		};
		let dataSource: kendo.data.DataSource = new kendo.data.DataSource(
			dataSourceOptions,
		);

		// let kendoGrid: kendo.ui.Grid = $('#genericGrid').data('kendoGrid');
		let kendoGrid: kendo.ui.Grid = $("#" + this.gridName).data("kendoGrid");

		if (kendoGrid !== undefined) {
			kendoGrid.destroy();
		}

		let gridOptions: kendo.ui.GridOptions = {};
		gridOptions.dataSource = dataSource;
		gridOptions.sortable = true;
		gridOptions.selectable = true;
		gridOptions.reorderable = true;
		gridOptions.scrollable = true;
		gridOptions.noRecords = {
			template: SharedMethodsModule.noDataTemplate(),
		};
		gridOptions.pageable = false;
		if (window.innerWidth > 768) {
			gridOptions.filterMenuInit = CustomGridFilterModule.onFilterMenuInit;
		}
		gridOptions.filterMenuInit = function (e) {
			if (e.container.find("select:eq(0)>option").length > 1) {
				e.container.find("select:eq(0)>option")[1].remove();
				//e.container.find("select:eq(1)>option")[1].remove();
				//e.container.find("select:eq(2)>option")[0].remove();
			}
		};
		gridOptions.groupable = {
			messages: {
				empty:
					this._localize.getLocalizedResource("Res2921") ||
					"Prevucite zaglavlje kolone i spustite ga ovde da biste grupisali po toj koloni",
			},
		};
		gridOptions.resizable = true;
		gridOptions.filterable = {
			mode: "multiple",
			messages: {
				selectedItemsFormat: (() => {
					const res = this._localize.getLocalizedResource("Res569");
					if (!res) return "{0} stavki izabrano";

					return res.indexOf("{0}") !== -1 ? res : "{0} " + res;
				})(),
				checkAll:
					this._localize.getLocalizedResource("Res2919") || "Izaberi sve",
				info:
					this._localize.getLocalizedResource("Res2903") ||
					"Prikaži redove sa vrednošću koja:",
				filter: this._localize.getLocalizedResource("Res2902") || "Filtriraj",
				clear: this._localize.getLocalizedResource("Res2918") || "Očisti",
				and: this._localize.getLocalizedResource("Res2916") || "I",
				or: this._localize.getLocalizedResource("Res2917") || "Ili",
				selectValue:
					this._localize.getLocalizedResource("Res2920") ||
					"-Izaberi vrednost-",
			},
			operators: {
				string: {
					eq: this._localize.getLocalizedResource("Res2904") || "Jednako je",
					neq: this._localize.getLocalizedResource("Res2905") || "Nije jednako",
					isnull: this._localize.getLocalizedResource("Res2910") || "Je NULL",
					isnotnull:
						this._localize.getLocalizedResource("Res2911") || "Nije NULL",
					startswith:
						this._localize.getLocalizedResource("Res2906") || "Počinje sa",
					contains: this._localize.getLocalizedResource("Res2907") || "Sadrži",
				},
				number: {
					eq: this._localize.getLocalizedResource("Res2904") || "Jednako je",
					gt: this._localize.getLocalizedResource("Res495") || "Veće od",
					lt: this._localize.getLocalizedResource("Res496") || "Manje od",
					neq: this._localize.getLocalizedResource("Res2905") || "Nije jednako",
					isnull: this._localize.getLocalizedResource("Res2910") || "Je NULL",
					isnotnull:
						this._localize.getLocalizedResource("Res2911") || "Nije NULL",
				},
				date: {
					eq: this._localize.getLocalizedResource("Res2904") || "Jednako je",
					neq: this._localize.getLocalizedResource("Res2905") || "Nije jednako",
					isnull: this._localize.getLocalizedResource("Res2910") || "Je NULL",
					isnotnull:
						this._localize.getLocalizedResource("Res2911") || "Nije NULL",
				},
				enums: {
					eq: this._localize.getLocalizedResource("Res2904") || "Jednako je",
					neq: this._localize.getLocalizedResource("Res2905") || "Nije jednako",
					isnull: this._localize.getLocalizedResource("Res2910") || "Je NULL",
					isnotnull:
						this._localize.getLocalizedResource("Res2911") || "Nije NULL",
				},
			},
		};
		gridOptions.columnMenu = {
			columns: false,
			messages: {
				sortAscending:
					this._localize.getLocalizedResource("Res2900") || "Sortiraj rastuće",
				sortDescending:
					this._localize.getLocalizedResource("Res2901") ||
					"Sortiraj opadajuće",
				filter: this._localize.getLocalizedResource("Res2902") || "Filter",
			},
		};
		gridOptions.toolbar = [
			"excel",
			"pdf",
			{
				template:
					"<button type='button' id='custom-grid-wizard-toggle' class='k-button k-button-icontext'><span class='k-icon custom-grid-wizard'></span>Grid Wizard</button>",
			},
		].filter((item) => item !== null);

		gridOptions.editable = {
			mode: "inline",
		};

		gridOptions.edit = function (e) {
			$("#" + this.gridName)
				.data("kendoGrid")
				.cancelChanges();
			if (e.model.isNew()) {
				this.onSelect("new");
			} else {
				const entityId = e.model[this.keyFields[0]];
				this.selectedId = e.model[entityId];
				this.onSelect(entityId);
			}
		}.bind(this);

		gridOptions.remove = function (evt) {
			$("#" + this.gridName)
				.data("kendoGrid")
				.cancelChanges();
			const entityId = evt.model[this.keyFields[0]];
			this.deleteItem(entityId);
		}.bind(this);

		gridOptions.columnReorder = customGroupHeader.gridColumnReorder;
		//columnDefs.push( { command: [{ name: "edit", text: "edit" }], title: "&nbsp;", width: "40px" });
		if (this.canAdd) {
			gridOptions.toolbar.unshift({
				template: `<button type='button' class='k-button k-button-icontext custom-add-new'>
               <span class='k-icon k-i-plus'></span> ADD NEW
             </button>`,
			});
		}
		if (this.canDelete && !appConfig.IsTraderApp) {
			//columnDefs.push( { command: ["edit", "destroy"], title: "&nbsp;", width: "250px" });
			(columnDefs.push({
				command: [{ name: "edit", className: "k-icon k-i-edit" }],
				title: "&nbsp;",
				width: 25,
			}),
				columnDefs.push({
					command: [{ name: "delete", className: "k-icon k-i-delete" }],
					title: "&nbsp;",
					width: 25,
				}));
		}

		if (appConfig.IsTraderApp && this.metadataName === "OrdersWindowWeb") {
			columnDefs.push({
				title: "&nbsp;",
				width: 40,
				template: (dataItem: any) => {
					const status = dataItem.OrderStatusID;
					const cancellable =
						status === -1 ||
						status === 0 ||
						status === 100 ||
						(appConfig.IsTraderApp && status === 1);
					if (cancellable) {
						return '<button class="k-button trader-cancel-order-btn" title="Otkaži nalog">✕</button>';
					}
					return "";
				},
			} as any);
		}

		if (this.metadataName === "OrdersWindowWeb") {
			columnDefs.push({
				title: "&nbsp;",
				width: 40,
				filterable: false,
				sortable: false,
				groupable: false,
				template:
					'<button class="order-print-btn" title="Print" style="background:none;border:none;cursor:pointer;padding:2px;color:inherit;display:flex;align-items:center;justify-content:center;width:100%;">' +
					'<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
					'<polyline points="6 9 6 2 18 2 18 9"/>' +
					'<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>' +
					'<rect x="6" y="14" width="12" height="8"/>' +
					"</svg>" +
					"</button>",
			} as any);
		}

		columnDefs.forEach((col) => {
			if (col.field) {
				col.groupHeaderTemplate = `#= getGroupInfo(aggregates, data, getKendoGrid('${this.gridName}')) #`;
			}
		});
		gridOptions.columns = columnDefs;
		this.setColumnAttributes(gridOptions.columns, metadataDetails);

		gridOptions.dataBound = customGroupHeader.KendoGridDataBound;

		// SharedMethodsModule.saveGridOptionsOnChange(gridOptions, this.gridName, this.metadataName)

		// const savedOptions = SharedMethodsModule.getSavedGridOptions(columnDefs, this.gridName, this.metadataName)
		// const combinedGridOptions = SharedMethodsModule.combineGridInitOptions(savedOptions, gridOptions)

		$("#" + this.gridName).kendoGrid(gridOptions);
		kendoGrid = $("#" + this.gridName).data("kendoGrid");

		$("#" + this.gridName).off("click", ".custom-add-new");
		$("#" + this.gridName).on("click", ".custom-add-new", (e) => {
			e.preventDefault();
			this._zone.run(() => {
				this._router.navigate(["new"], { relativeTo: this.route });
			});
		});

		if (appConfig.IsTraderApp && this.metadataName === "OrdersWindowWeb") {
			$("#" + this.gridName).off("click", ".trader-cancel-order-btn");
			$("#" + this.gridName).on("click", ".trader-cancel-order-btn", (e) => {
				e.preventDefault();
				e.stopPropagation();
				const grid = $("#" + this.gridName).data("kendoGrid");
				const row = $(e.target).closest("tr");
				let dataItem = grid.dataItem(row);
				if (!dataItem) {
					const row2 = $(e.currentTarget).closest("tr");
					dataItem = grid.dataItem(row2);
				}
				if (!dataItem) {
					console.warn("[CancelOrder] dataItem not found for row", row);
					return;
				}
				const keyField =
					this.keyFields && this.keyFields.length > 0
						? this.keyFields[0]
						: null;
				const orderIndex = keyField
					? dataItem[keyField]
					: (dataItem["OrderIndex"] ?? dataItem["OrderID"] ?? dataItem["ID"]);
				if (orderIndex == null) {
					console.warn(
						"[CancelOrder] orderIndex not found on dataItem",
						dataItem,
					);
					return;
				}

				this._pendingCancelOrderIndex = orderIndex;
				($("#cancel-order-confirm-modal") as any).modal({ show: true });
			});
		}

		if (this.metadataName === "OrdersWindowWeb") {
			$("#" + this.gridName).off("click", ".order-print-btn");
			$("#" + this.gridName).on("click", ".order-print-btn", (e) => {
				e.preventDefault();
				e.stopPropagation();
				const grid = $("#" + this.gridName).data("kendoGrid");
				const row = $(e.target).closest("tr");
				let dataItem = grid.dataItem(row);
				if (!dataItem) {
					const row2 = $(e.currentTarget).closest("tr");
					dataItem = grid.dataItem(row2);
				}
				if (!dataItem) {
					console.warn("[OrderPrint] dataItem not found for row", row);
					return;
				}
				const keyField =
					this.keyFields && this.keyFields.length > 0
						? this.keyFields[0]
						: null;
				const orderIndex = keyField
					? dataItem[keyField]
					: (dataItem["OrderIndex"] ?? dataItem["OrderID"] ?? dataItem["ID"]);
				if (orderIndex == null) {
					console.warn(
						"[OrderPrint] orderIndex not found on dataItem",
						dataItem,
					);
					return;
				}
				// OrderStatusId === 4 means the order was cancelled at some point,
				// so the user can choose between the regular order confirmation
				// and the cancellation confirmation report.
				if (Number(dataItem["OrderStatusID"]) === 4) {
					this._pendingReportOrderIndex = orderIndex;
					($("#order-report-choice-modal") as any).modal({ show: true });
					return;
				}
				this.openOrderBookPdf(orderIndex);
			});
		}
		// if (savedOptions && savedOptions.dataSource) {
		//   $('#' + this.gridName).data('kendoGrid').dataSource.group(savedOptions.dataSource.group)
		//   $('#' + this.gridName).data('kendoGrid').dataSource.sort(savedOptions.dataSource.sort)
		// }

		// The first build can finish before the surrounding flex layout and the
		// localization pass (ngAfterViewInit) have settled, leaving column headers
		// blank and widths miscomputed. Re-apply translations to the freshly built
		// grid and recompute its layout once the current tick completes.
		setTimeout(() => {
			const grid = $("#" + this.gridName).data("kendoGrid");
			if (!grid) return;
			this._localize.applyTranslations(this._element.nativeElement);
			grid.resize(true);
		}, 0);

		if (this.metadataName === "TaskList") {
			dataSource.filter({
				logic: "IsEqualTo",
				// filters: [{ field: 'TaskCompleted', value: false }],
				filters: [{ field: "Done", value: false }],
			});

			dataSource.group({
				// field: 'AssignedUserID',
				field: "AssignedTo",
			});

			dataSource.sort([{ field: "DueDate", dir: "asc" }]);
		} else if (this.metadataName === "PortalLoginStats") {
			dataSource.sort([{ field: "SessionStart", dir: "desc" }]);
		} else if (this.metadataName === "OrdersWindowWeb") {
			dataSource.sort([{ field: "OrderDate", dir: "desc" }]);
		}

		setTimeout(() => {
			$("#custom-grid-wizard-toggle").off("click", this.openGridWizard);
			$("#custom-grid-wizard-toggle").click(this.openGridWizard);
		}, 10);
	}

	setColumnAttributes(
		columns: kendo.ui.GridColumn[],
		metadataDetails: ParsedFieldMetadataDetail[],
	) {
		columns.forEach((col) => {
			if (col.field) {
				const md = metadataDetails.find((md) => md.FieldName === col.field);
				if (md.JavascriptType === "number") {
					switch (md.DataType) {
						case "real":
						case "money":
						case "float":
						case "decimal":
						case "smallint":
						case "short":
						case "double":
							const style = "text-align: right;";
							if (col.attributes) {
								col.attributes.style = col.attributes.style
									? col.attributes.style + style
									: style;
							} else {
								col.attributes = { style };
							}

							if (col.headerAttributes) {
								col.headerAttributes.style = col.headerAttributes.style
									? col.headerAttributes.style + style
									: style;
							} else {
								col.headerAttributes = { style };
							}

							if (col.footerAttributes) {
								col.footerAttributes.style = col.footerAttributes.style
									? col.footerAttributes.style + style
									: style;
							} else {
								col.footerAttributes = { style };
							}
					}
				}
			}
		});
	}

	handleError(message?) {
		this.showModal({
			title: "Oops!",
			message: message || "Something went wrong",
		});
		this.loadingStopped();
	}

	loadingStarted() {
		kendo.ui.progress($(".genericForm-loading"), true);
		$("#" + this.gridName).addClass("k-state-disabled");
	}

	loadingStopped() {
		kendo.ui.progress($(".genericForm-loading"), false);
		$("#" + this.gridName).removeClass("k-state-disabled");
	}

	ngOnInit() {
		document.getElementById("content").classList.add("flex-override"); // fixes vertical overflow issue

		// Reload grid when the global filter (dates, portfolio) changes.
		// This also covers the case where the grid was loaded before dates were
		// initialised (appStateProvider.startDate was undefined at ngOnChanges time).
		this._appStateSubscription = this._appStateProvider.subscribe(() => {
			if (!this.metadataName) return;

			const fromDate: Date = this._appStateProvider.startDate
				|| new Date(new Date().getFullYear() - 1, 11, 31);
			const toDate: Date = this._appStateProvider.endDate || new Date();

			const prevFrom = this._lastFromDate ? this._lastFromDate.getTime() : null;
			const prevTo = this._lastToDate ? this._lastToDate.getTime() : null;
			const nextFrom = fromDate ? fromDate.getTime() : null;
			const nextTo = toDate ? toDate.getTime() : null;

			if (prevFrom === nextFrom && prevTo === nextTo) return;

			this._lastFromDate = fromDate;
			this._lastToDate = toDate;
			this.loadingStarted();
			this.loadMetadata(0, fromDate, toDate);
		});
	}

	ngAfterViewInit() {
		this._localize.applyTranslations(this._element.nativeElement);
	}

	ngOnChanges(changes) {
		// CLEAR GRID
		const kendoGrid = $("#" + this.gridName).data("kendoGrid");
		if (kendoGrid) {
			kendoGrid.destroy();
		}
		$("#" + this.gridName).empty();

		if (changes.metadataName && changes.metadataName.currentValue) {
			this.loadingStarted();
			const fromDate: Date = this._appStateProvider.startDate
				|| new Date(new Date().getFullYear() - 1, 11, 31);
			const toDate: Date = this._appStateProvider.endDate || new Date();
			this._lastFromDate = fromDate;
			this._lastToDate = toDate;
			this.loadMetadata(0, fromDate, toDate);
		} else if (this.data) {
			$("#" + this.gridName).kendoGrid({
				columnMenu: true,
				filterable: true,
				sortable: true,
				dataSource: { data: this.data },
			});
			this.loadingStopped();
		} else {
			console.error(
				"GenericGrid requires either data, or metadataName property",
			);
		}
	}

	openOrderReport(reportType: "book" | "withdraval"): void {
		($("#order-report-choice-modal") as any).modal("hide");
		if (this._pendingReportOrderIndex != null) {
			this.openOrderBookPdf(this._pendingReportOrderIndex, reportType);
			this._pendingReportOrderIndex = null;
		}
	}

	confirmCancelOrder(): void {
		($("#cancel-order-confirm-modal") as any).modal("hide");
		if (this._pendingCancelOrderIndex != null) {
			this._executeCancelOrder(this._pendingCancelOrderIndex);
			this._pendingCancelOrderIndex = null;
		}
	}

	private _executeCancelOrder(orderIndex: any): void {
		const headers = this._authService.getAuthHeaders("application/json");
		const url =
			appConfig.insaServiceAddress +
			"/Trader/TraderOrderCancel?orderIndex=" +
			orderIndex;
		this._http
			.post<{ success: boolean; description: string }>(url, null, { headers })
			.subscribe({
				next: (res) => {
					const isSuccess = res && res.success;
					const message = isSuccess
						? this._localize.getLocalizedResource("Res568") ||
							"Nalog je uspješno opozvan."
						: this._localize.getLocalizedResource("Res2924") ||
							"Otkazivanje naloga nije uspjelo.";
					this._showCancelNotification(isSuccess, message);
					if (isSuccess) {
						this.loadMetadata(0, this._lastFromDate, this._lastToDate);
					}
				},
				error: (err) => {
					console.error("[CancelOrder] error:", err);
					const message =
						this._localize.getLocalizedResource("Res2924") ||
						"Otkazivanje naloga nije uspjelo.";
					this._showCancelNotification(false, message);
				},
			});
	}

	private _showCancelNotification(success: boolean, message: string): void {
		const icon = success ? "✓" : "✕";
		const typeClass = success ? "success" : "error";
		const el = $(`
      <div class="order-cancel-notification ${typeClass}">
        <span class="notif-icon">${icon}</span>
        <div class="notif-body">
          <div class="notif-msg">${message}</div>
        </div>
      </div>
    `).appendTo("body");
		setTimeout(() => el.remove(), 10000);
	}

	private loadMetadata(attempt: number, fromDate?: Date, toDate?: Date) {
		// A new load supersedes any in-flight one. Cancel the previous subscription
		// (drops its pending HTTP) and stamp this load so its async callbacks can tell
		// whether they are still the latest before they touch the grid DOM.
		const token = ++this._loadToken;
		if (this._metadataSubscription) {
			this._metadataSubscription.unsubscribe();
			this._metadataSubscription = null;
		}

		const metadataObservable = this._insaMetadataService.getMetaDataInformation(
			this.metadataName,
			fromDate,
			toDate,
		);
		const defaultLayoutObservable: Observable<kendo.ui.GridOptions> =
			this._authService.loadDefaultGridLayout(this.gridName, this.metadataName);

		this._metadataSubscription = combineLatest([metadataObservable, defaultLayoutObservable]).subscribe(
			([metadataSet, defaultLayout]) => {
				if (token !== this._loadToken) return; // a newer load started — ignore stale result
				if (!metadataSet) {
					this.handleError(`No metadata received for "${this.metadataName}"`);
					return;
				}
				const parsedMetadataSet = makeParsedMetadataSet(
					metadataSet,
					this._localize.getLocalizedResource.bind(this._localize),
				);
				this.keyFields = computeKeyFields(
					parsedMetadataSet[0].InsaMetaDataDetails,
				);
				this.metadataSet = parsedMetadataSet;

				const details = parsedMetadataSet[0].InsaMetaDataDetails;
				const primaryKeyMetadata = details.find(
					(md) => String(md.IsKey).toLowerCase() === "true",
				);
				if (primaryKeyMetadata) {
					const recordSet = metadataSet[1].MetaDataSelectedEntity;
					// Fix: pass metadataDetails (array), not the whole metadataSet tuple
					let gridSource = this.prepareGridSource(recordSet, details);
					const pkField = primaryKeyMetadata.FieldName;
					const values = gridSource.data
						.map((row) => row[pkField])
						.filter((val) => typeof val === "number");

					const max = Math.max(...values, 0);
					this._gridStateService.setMaxPrimaryKeyValue(max);
				}

				if (!this.keyFields.length) {
					console.warn(`${this.metadataName} metadata has no key field`);
				}
				$("#" + this.gridName).empty();
				// defaultLayout is always a valid object ({name:'default'} or saved layout)
				this.prepareGenericForm(
					parsedMetadataSet,
					defaultLayout || { name: "default" },
				);
				this.loadingStopped();
			},
			(err) => {
				if (token !== this._loadToken) return; // a newer load started — ignore stale error
				if (attempt < 1) {
					// First failure: silently retry after 2 s to let auth/session
					// initialisation finish (common race on direct-URL navigation).
					console.warn(
						`GenericGrid: metadata load failed (attempt ${attempt + 1}), retrying in 2s…`,
						err,
					);
					setTimeout(
						() => this.loadMetadata(attempt + 1, fromDate, toDate),
						2000,
					);
				} else {
					// Second failure with dates present: give up and show the error modal.
					$("#" + this.gridName).empty();
					this.handleError(err);
				}
			},
		);
	}

	private openOrderBookPdf(
		orderIndex: any,
		reportType: "book" | "withdraval" = "book",
	) {
		$("#order-pdf-overlay").remove();
		if (this._orderPdfBlobUrl) {
			URL.revokeObjectURL(this._orderPdfBlobUrl);
			this._orderPdfBlobUrl = null;
		}

		const isMobile =
			/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
			window.innerWidth < 768;
		const openLabel =
			this._localize.getLocalizedResource("Res4501") || "Open";

		const bodyStyle = isMobile
			? "flex:1;display:flex;align-items:center;justify-content:center;overflow:hidden;padding:2rem;"
			: "flex:1;display:flex;align-items:center;justify-content:center;overflow:hidden;";

		const reportTitle =
			reportType === "withdraval"
				? "Order Cancellation Report"
				: "Order Book Report";
		const reportFileNamePrefix =
			reportType === "withdraval" ? "order_cancel_" : "order_";

		const overlay = $(`
      <div id="order-pdf-overlay" style="
        position:fixed;inset:0;z-index:9999;
        background:rgba(0,0,0,0.65);
        display:flex;align-items:center;justify-content:center;
        padding:1rem;">
        <div id="order-pdf-modal" style="
          background:#fff;border-radius:0.5rem;
          box-shadow:0 8px 40px rgba(0,0,0,0.4);
          display:flex;flex-direction:column;
          width:90vw;max-width:${isMobile ? "480px" : "960px"};height:${isMobile ? "auto" : "88vh"};">
          <div id="order-pdf-header" style="
            display:flex;align-items:center;justify-content:space-between;
            padding:0.75rem 1rem;border-bottom:1px solid #ddd;flex-shrink:0;">
            <span style="font-weight:600;font-size:0.95rem;">
              ${reportTitle} #${orderIndex}
            </span>
            <button id="order-pdf-close" style="
              background:none;border:none;font-size:1.25rem;
              cursor:pointer;line-height:1;padding:0 0.25rem;color:#555;">✕</button>
          </div>
          <div id="order-pdf-body" style="${bodyStyle}">
            <div id="order-pdf-spinner" style="text-align:center;">
              <div style="
                width:2.5rem;height:2.5rem;border-radius:50%;
                border:3px solid #e0e0e0;border-top-color:#230078;
                animation:orderPdfSpin 0.8s linear infinite;margin:0 auto 0.75rem;">
              </div>
              <span style="color:#888;font-size:0.875rem;">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    `);

		if (!document.getElementById("order-pdf-spin-style")) {
			const style = document.createElement("style");
			style.id = "order-pdf-spin-style";
			style.textContent =
				"@keyframes orderPdfSpin { to { transform: rotate(360deg); } }";
			document.head.appendChild(style);
		}

		$("body").append(overlay);

		$("#order-pdf-close, #order-pdf-overlay").on("click", (e) => {
			if (
				e.target === document.getElementById("order-pdf-overlay") ||
				$(e.target).is("#order-pdf-close")
			) {
				this.closeOrderPdf();
			}
		});

		const report$ =
			reportType === "withdraval"
				? this._insaService.getOrderWithdravalReport(orderIndex)
				: this._insaService.getOrderBookReport(orderIndex);

		report$.subscribe(
			(data: string) => {
				if (!data) {
					this.showOrderPdfError("No data returned from server.");
					return;
				}
				try {
					const byteChars = atob(data);
					const byteNumbers = new Uint8Array(byteChars.length);
					for (let i = 0; i < byteChars.length; i++) {
						byteNumbers[i] = byteChars.charCodeAt(i);
					}
					const blob = new Blob([byteNumbers], { type: "application/pdf" });
					this._orderPdfBlobUrl = URL.createObjectURL(blob);
					if (isMobile) {
						$("#order-pdf-spinner").replaceWith(`
							<div style="text-align:center;padding:1.5rem 2rem 2rem;">
								<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#888" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="margin-bottom:1rem;">
									<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
								</svg>
								<br>
								<a href="${this._orderPdfBlobUrl}" download="${reportFileNamePrefix}${orderIndex}.pdf"
								   style="display:inline-block;background:#230078;color:#fff;padding:0.6rem 2rem;border-radius:0.375rem;text-decoration:none;font-weight:600;font-size:0.95rem;">
									${openLabel}
								</a>
							</div>`);
					} else {
						$("#order-pdf-spinner").replaceWith(
							`<iframe src="${this._orderPdfBlobUrl}" style="width:100%;height:100%;border:none;border-radius:0 0 0.5rem 0.5rem;"></iframe>`,
						);
					}
				} catch (err) {
					this.showOrderPdfError("Failed to render PDF.");
				}
			},
			() => this.showOrderPdfError("Request failed."),
		);
	}

	private showOrderPdfError(message: string) {
		$("#order-pdf-spinner").html(
			`<div style="text-align:center;color:#c12a23;padding:2rem;">
        <div style="font-size:2rem;margin-bottom:0.5rem;">✕</div>
        <div style="font-size:0.9rem;">${message}</div>
       </div>`,
		);
	}

	private closeOrderPdf() {
		$("#order-pdf-overlay").remove();
		if (this._orderPdfBlobUrl) {
			URL.revokeObjectURL(this._orderPdfBlobUrl);
			this._orderPdfBlobUrl = null;
		}
	}

	ngOnDestroy() {
		if (this._appStateSubscription) {
			this._appStateSubscription.unsubscribe();
		}
		if (this._metadataSubscription) {
			this._metadataSubscription.unsubscribe();
			this._metadataSubscription = null;
		}
		document.getElementById("content").classList.remove("flex-override"); // fixes vertical overflow issue

		// remove event listeners
		$("#" + this.gridName).off("dblclick", this.doubleClickHandler);
		$("html").off("keydown", this.delPressHandler);
	}
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-generic-form/generic-grid/generic-grid.component.ts